import { z } from 'zod';

// Zod schema that matches the backend DTO requirements
export const accountingCenterSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  acmev: z.number().min(0, { message: 'ACMEV must be a positive number' }),
  acmev_alert_threshold: z.number().min(0, { message: 'ACMEV alert threshold must be a positive number' }),
  acrt: z.number().min(0, { message: 'ACRT must be a positive number' }),
  acrt_alert_threshold: z.number().min(0, { message: 'ACRT alert threshold must be a positive number' })
});

// Member of an accounting center (sourced from the parent budget members).
// `oreHours` is editable, `hourlyRate` is read-only, and `maxOreHours` is the
// per-member cap that will come from the API (placeholder until integrated).
export const accountingCenterMemberSchema = z
  .object({
    memberId: z.string().optional().default(''),
    userId: z.string().default(''),
    email: z.string(),
    memberName: z.string().default(''),
    profilePicUrl: z.string().nullable().optional().default(null),
    type: z.string().default(''),
    oreHours: z.preprocess(
      (val) => {
        if (val === '' || val === null || val === undefined) return undefined;
        const num = Number(val);
        return isNaN(num) ? undefined : num;
      },
      z
        .number({ required_error: 'Ore hours is required', invalid_type_error: 'Ore hours is required' })
        .min(0, { message: 'Ore hours must be 0 or greater' })
    ),
    hourlyRate: z.preprocess(
      (val) => {
        if (val === '' || val === null || val === undefined) return 0;
        const num = Number(val);
        return isNaN(num) ? 0 : num;
      },
      z.number().min(0, { message: 'Hourly rate must be 0 or greater' })
    ),
    maxOreHours: z.number().optional()
  })
  .superRefine((member, ctx) => {
    if (member.maxOreHours != null && Number.isFinite(member.maxOreHours) && member.oreHours > member.maxOreHours) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['oreHours'],
        message: `Cannot exceed ${member.maxOreHours} Hours`
      });
    }
  });

export type AccountingCenterMemberFormValues = z.infer<typeof accountingCenterMemberSchema>;

// Helper to build acmev/acrt schema with "cannot exceed" max (like initialAmount but with <= max)
const createAmountSchema = (fieldName: string, maxAmount: number | undefined, maxLabel: string) =>
  z
    .union([z.string(), z.number()])
    .refine((val) => val !== '' && val !== null && val !== undefined, {
      message: `${fieldName} is required`
    })
    .transform((val) => (typeof val === 'string' ? Number(val) : val))
    .refine((val) => !isNaN(val), {
      message: `${fieldName} must be a valid number`
    })
    .refine((val) => val >= 0, {
      message: `${fieldName} must be 0 or greater`
    })
    .refine((val) => maxAmount == null || !Number.isFinite(maxAmount) || val <= maxAmount, {
      message:
        maxAmount != null && Number.isFinite(maxAmount)
          ? `${fieldName} cannot exceed ${maxLabel} (€${maxAmount.toLocaleString()})`
          : `${fieldName} must be a positive number`
    });

// Form schema for the frontend form (static, no max caps)
export const accountingCenterFormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  acmev: createAmountSchema('ACMEV', undefined, 'BMEV'),
  acmev_alert_threshold: z.union([
    z.number().min(0, { message: 'ACMEV alert threshold must be a positive number' }),
    z
      .string()
      .min(1, { message: 'ACMEV alert threshold is required' })
      .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: 'ACMEV alert threshold must be a positive number'
      })
  ]),
  acrt: createAmountSchema('ACRT', undefined, 'BTRT'),
  acrt_alert_threshold: z.union([
    z.number().min(0, { message: 'ACRT alert threshold must be a positive number' }),
    z
      .string()
      .min(1, { message: 'ACRT alert threshold is required' })
      .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: 'ACRT alert threshold must be a positive number'
      })
  ]),
  members: z.array(accountingCenterMemberSchema).default([])
});

// Dynamic form schema with max ACMEV and max ACRT (cannot exceed)
export const createAccountingCenterFormSchema = (maxAcmev: number | undefined, maxAcrt: number | undefined) =>
  z.object({
    name: z.string().min(1, { message: 'Name is required' }),
    description: z.string().min(1, { message: 'Description is required' }),
    acmev: createAmountSchema('ACMEV', maxAcmev, 'BMEV'),
    acmev_alert_threshold: z.union([
      z.number().min(0, { message: 'ACMEV alert threshold must be a positive number' }),
      z
        .string()
        .min(1, { message: 'ACMEV alert threshold is required' })
        .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
          message: 'ACMEV alert threshold must be a positive number'
        })
    ]),
    acrt: createAmountSchema('ACRT', maxAcrt, 'BTRT'),
    acrt_alert_threshold: z.union([
      z.number().min(0, { message: 'ACRT alert threshold must be a positive number' }),
      z
        .string()
        .min(1, { message: 'ACRT alert threshold is required' })
        .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
          message: 'ACRT alert threshold must be a positive number'
        })
    ]),
    members: z.array(accountingCenterMemberSchema).default([])
  });

export const getACMembersStepFieldNames = (memberCount: number): string[] => {
  const fields: string[] = [];
  for (let index = 0; index < memberCount; index++) {
    fields.push(`members.${index}.oreHours`);
  }
  return fields;
};

export type AccountingCenterFormValues = z.infer<typeof accountingCenterFormSchema>;

/** Member embedded in AC responses */
export interface ACMemberEntry {
  member_id: string;
  user_id: string;
  name: string;
  email: string;
  profile_pic_url: string | null;
  role: 'OWNER' | 'VALIDATOR' | 'STAFF';
  ore_hours: number;
  hourly_rate: number;
  acoh?: number;
  acoh_used?: number;
}

export interface AccountingCenter {
  _id: string;
  name: string;
  description: string;
  type: string;
  acmev: number;
  acmev_alert_threshold: number;
  acrt: number;
  acrt_alert_threshold: number;
  achov?: number;
  achov_used?: number;
  acoh?: number;
  acoh_used?: number;
  is_default?: boolean;
  budgetId: string;
  members?: ACMemberEntry[];
  [key: string]: any;
}

export interface MemberAllocationItem {
  member_id: string;
  ore_hours: number;
}

// Request Data interfaces
export type AccountingCenterCreationData = z.infer<typeof accountingCenterSchema>;

/** Extended creation payload with AC type + optional member hour allocations */
export interface AccountingCenterCreationWithAllocationsData extends AccountingCenterCreationData {
  type?: string;
  member_allocations?: MemberAllocationItem[];
}

export interface AccountingCenterCreationResponse {
  success: boolean;
  message: string;
  data: AccountingCenter;
}

export type AccountingCenterUpdateData = Partial<AccountingCenterCreationData>;

/** Extended update payload with AC type + optional member hour allocations */
export interface AccountingCenterUpdateWithAllocationsData extends AccountingCenterUpdateData {
  type?: string;
  member_allocations?: MemberAllocationItem[];
}

export interface AccountingCenterUpdateResponse {
  success: boolean;
  message: string;
  data: AccountingCenter;
}

export interface AccountingCenterResponse {
  success: boolean;
  message: string;
  data: AccountingCenter;
}

export interface AccountingCentersResponse {
  success: boolean;
  message: string;
  data: AccountingCenter[];
}
